import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../mailService";
import "../assets/css/order-status.css";

// Timeline steps. The pole step only appears when the order includes poles.
// Steps progress from the order status (Quotation/Accepted/Completed) and the
// production events staff log from the admin "Manage TimeLine" dialog.
const buildSteps = (hasPoles) =>
  [
    { icon: "pi-shopping-cart", label: "Confirmed Order", key: "confirmed" },
    { icon: "pi-cog", label: "Processing Order", key: "processing" },
    hasPoles && { icon: "pi-wrench", label: "Poles Making Done", key: "poles" },
    { icon: "pi-check-circle", label: "Finished Sewing", key: "sewing" },
    { icon: "pi-home", label: "Order Completed", key: "completed" },
  ].filter(Boolean);

const eventToStepKey = {
  "Done Poles Making": "poles",
  "Finished Sewing": "sewing",
  "Order Completed": "completed",
};

const getCurrentStepIndex = (order, steps) => {
  const reached = new Set(["confirmed"]);

  if (order.status === "Accepted" || order.status === "Completed") {
    reached.add("processing");
  }
  if (order.status === "Completed") {
    reached.add("completed");
  }

  (order.events || []).forEach((e) => {
    const key = eventToStepKey[e.eventName];
    if (key) reached.add(key);
  });

  let index = -1;
  steps.forEach((step, i) => {
    if (reached.has(step.key)) index = i;
  });
  return index;
};

const statusLabels = {
  Quotation: "Confirmed Order",
  Accepted: "Processing Order",
  Completed: "Product Delivered",
  OnHold: "On Hold",
  Rejected: "Rejected",
};

const formatMoney = (value) =>
  (value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return isNaN(date) ? "" : date.toLocaleDateString("en-GB");
};

const OrderTracking = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/orders/track`, { params: { id } })
      .then((res) => setOrder(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  const steps = buildSteps(order?.hasPoles);
  const currentStatusIndex = order ? getCurrentStepIndex(order, steps) : -1;

  let statusLabel = "";
  if (order) {
    const isExceptional = order.status === "OnHold" || order.status === "Rejected";
    statusLabel =
      !isExceptional && currentStatusIndex >= 0
        ? steps[currentStatusIndex].label
        : statusLabels[order.status] ?? order.status;
  }

  if (loading) {
    return (
      <div className="order-tracking-container">
        <div className="tracking-header-div">
          <h3 className="tracking-header">Geeth Curtains</h3>
          <h5 className="tracking-sub-header">
            Innovative Options for the Modern World
          </h5>
        </div>
        <div className="flex justify-content-center align-items-center p-6">
          <i className="pi pi-spin pi-spinner text-4xl"></i>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-tracking-container">
        <div className="tracking-header-div">
          <h3 className="tracking-header">Geeth Curtains</h3>
          <h5 className="tracking-sub-header">
            Innovative Options for the Modern World
          </h5>
        </div>
        <div className="flex flex-column align-items-center p-6 gap-3">
          <h3 className="tracking-header">Order Not Found</h3>
          <p>
            We could not find this order. Please check the link or contact us
            on +94 71 802 1976.
          </p>
          <div
            className="view-all cursor-pointer text-color"
            onClick={() => navigate("/gallery")}
          >
            View Our Gallery
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-tracking-container">
      <div className="tracking-header-div">
        <h3 className="tracking-header">Geeth Curtains</h3>
        <h5 className="tracking-sub-header">
          Innovative Options for the Modern World
        </h5>
      </div>

      <div className="flex justify-content-center align-items-center">
        <div className="mb-5 col-12 lg:col-9">
          <div className="detail-header-2">
            <h3>Customer Details</h3>
          </div>
          <div className="">
            <label>Customer Name : {order.customerName}</label>
          </div>
          <div className="">
            <label>Telephone / Whatsapp Number : {order.telephone}</label>
          </div>
          <div className="">
            <label>Email : {order.email}</label>
          </div>
          <div className="">
            <label>Address : {order.address}</label>
          </div>
        </div>
      </div>

      <div>
        <h3 className="tracking-header">TRACKING YOUR ORDER</h3>
        <h3 className="order-no">Order NO - {order.orderNo}</h3>
      </div>

      <div className="order-info flex justify-content-between align-items-center p-3 bg-gray-100">
        <span>Order Date: {formatDate(order.created)}</span>
        <span>Status: {statusLabel}</span>
        <span>
          Expected Date: {formatDate(order.deliveryDate)}
        </span>
      </div>

      <div className="tracking-timeline flex align-items-center justify-content-between mt-4 px-4">
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <div className="timeline-item text-center">
              <div
                className={`timeline-icon ${
                  index <= currentStatusIndex ? "active" : ""
                }`}
              >
                <i className={`pi ${step.icon}`}></i>
              </div>
              <span className="mt-2 block">{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`timeline-line ${
                  index < currentStatusIndex ? "active" : ""
                }`}
              ></div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="flex justify-content-center align-items-center">
        <div className="order-details-status lg:col-9 mb-8">
          <div className="detail-header-2">
            <h3>Order Details</h3>
          </div>
          <div className="">
            <label>Total Quantities : {order.totalQty}</label>
          </div>
          <div className="">
            <label>Pole Width : {order.totalWidth} ft</label>
          </div>
          <div className="">
            <label>Total Pleats : {order.totalPleats}</label>
          </div>
          <hr />
          <div className="">
            <label>Fixing Cost : LKR {formatMoney(order.fixingCost)}</label>
          </div>
          <div className="">
            <label>
              Transportation Cost : LKR {formatMoney(order.transportationCost)}
            </label>
          </div>
          <hr />
          <div className="">
            <span>Total </span>
            <span>LKR {formatMoney(order.total)}</span>
          </div>
        </div>
      </div>

      <div className="main-div-2">
        <div className="flex flex-column">
          <hr />
          <div className="mb-4 flex flex-column align-items-center gap-3">
            <span
              className="cormorant-garamond bg-white lg:px-8 px-4 text-4xl font-medium"
              style={{ color: "#4f6461", marginTop: "-2rem" }}
            >
              Explore More
            </span>
            <div
              className="view-all cursor-pointer text-color"
              onClick={() => navigate("/gallery")}
            >
              View All
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
